import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useResourceHubListStore, type ResourceCategoryNode } from '../store/useResourceHubListStore';

const categoryHasResources = (category: ResourceCategoryNode, idsWithResources: Set<string>): boolean => {
  if (idsWithResources.has(category.documentId)) return true;
  if (category.subCategories?.length) {
    return category.subCategories.some((sub) => categoryHasResources(sub, idsWithResources));
  }
  return false;
};

interface CategoryPosition {
  left: number;
  width: number;
  isSelected: boolean;
}

export const useResourceHubFilters = () => {
  const {
    rootCategories,
    categoryIdsWithResources,
    selectedCategoryIds,
    selectedSubcategoryIds,
    searchQuery,
    setSearchQuery,
    toggleCategory,
    toggleSubcategory,
    resetFilters,
  } = useResourceHubListStore(
    useShallow((state) => ({
      rootCategories: state.rootCategories,
      categoryIdsWithResources: state.categoryIdsWithResources,
      selectedCategoryIds: state.selectedCategoryIds,
      selectedSubcategoryIds: state.selectedSubcategoryIds,
      searchQuery: state.searchQuery,
      setSearchQuery: state.setSearchQuery,
      toggleCategory: state.toggleCategory,
      toggleSubcategory: state.toggleSubcategory,
      resetFilters: state.resetFilters,
    })),
  );

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [categoryPositions, setCategoryPositions] = useState<CategoryPosition[]>([]);
  const [containerWidth, setContainerWidth] = useState(0);
  const [buttonsOffsetLeft, setButtonsOffsetLeft] = useState(0);

  const filteredRootCategories = useMemo(
    () => rootCategories.filter((cat) => categoryHasResources(cat, categoryIdsWithResources)),
    [rootCategories, categoryIdsWithResources],
  );

  const visibleSubcategories = useMemo(() => {
    if (selectedCategoryIds.length === 0) return [];
    const items: { subcategory: ResourceCategoryNode; parentColor: string | null | undefined }[] = [];
    for (const rootId of selectedCategoryIds) {
      const root = rootCategories.find((r) => r.documentId === rootId);
      if (root?.subCategories?.length) {
        for (const sub of root.subCategories) {
          if (categoryHasResources(sub, categoryIdsWithResources)) {
            items.push({ subcategory: sub, parentColor: root.color });
          }
        }
      }
    }
    return items;
  }, [selectedCategoryIds, rootCategories, categoryIdsWithResources]);

  const hasActiveFilters = searchQuery !== '' || selectedCategoryIds.length > 0 || selectedSubcategoryIds.length > 0;

  const updateCategoryPositions = useCallback(() => {
    if (!buttonsContainerRef.current || !wrapperRef.current) return;
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const containerRect = buttonsContainerRef.current.getBoundingClientRect();
    const positions: CategoryPosition[] = [];
    setContainerWidth(containerRect.width);
    setButtonsOffsetLeft(containerRect.left - wrapperRect.left);
    filteredRootCategories.forEach((category) => {
      const buttonEl = categoryRefs.current.get(category.documentId);
      if (buttonEl) {
        const buttonRect = buttonEl.getBoundingClientRect();
        positions.push({
          left: buttonRect.left - containerRect.left,
          width: buttonRect.width,
          isSelected: selectedCategoryIds.includes(category.documentId),
        });
      }
    });
    setCategoryPositions(positions);
  }, [filteredRootCategories, selectedCategoryIds]);

  useEffect(() => {
    const timer = setTimeout(updateCategoryPositions, 50);
    window.addEventListener('resize', updateCategoryPositions);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateCategoryPositions);
    };
  }, [updateCategoryPositions]);

  const setCategoryRef = useCallback(
    (id: string) => (el: HTMLButtonElement | null) => {
      if (el) categoryRefs.current.set(id, el);
      else categoryRefs.current.delete(id);
    },
    [],
  );

  const generateLineWithChevrons = useMemo(() => {
    if (categoryPositions.length === 0 || containerWidth === 0) return '';
    const lineY = 6;
    const peakY = 0;
    const chevronHalfWidth = 5;
    let pathD = `M 0 ${lineY}`;
    let currentX = 0;
    const sortedPositions = [...categoryPositions].sort((a, b) => a.left - b.left);
    for (const pos of sortedPositions) {
      const buttonCenterX = pos.left + pos.width / 2;
      const chevronStartX = buttonCenterX - chevronHalfWidth;
      const chevronEndX = buttonCenterX + chevronHalfWidth;
      if (pos.isSelected) {
        if (currentX < chevronStartX) pathD += ` L ${chevronStartX} ${lineY}`;
        pathD += ` L ${buttonCenterX} ${peakY}`;
        pathD += ` L ${chevronEndX} ${lineY}`;
        currentX = chevronEndX;
      }
    }
    if (currentX < containerWidth) pathD += ` L ${containerWidth} ${lineY}`;
    return pathD;
  }, [categoryPositions, containerWidth]);

  const categorySelectValue = useMemo(() => {
    if (selectedCategoryIds.length === 0) return '';
    if (selectedCategoryIds.length === 1) return selectedCategoryIds[0];
    return selectedCategoryIds[0];
  }, [selectedCategoryIds]);

  const subcategoryOptions = useMemo(
    () =>
      visibleSubcategories.map(({ subcategory }) => ({
        value: subcategory.documentId,
        label: subcategory.category,
      })),
    [visibleSubcategories],
  );

  const handleCategorySelect = useCallback(
    (value: string) => {
      if (!value) return;
      if (selectedCategoryIds[0] === value) return;
      if (selectedCategoryIds.length > 0) toggleCategory(selectedCategoryIds[0]);
      toggleCategory(value);
    },
    [selectedCategoryIds, toggleCategory],
  );

  const handleSubcategoryChange = useCallback(
    (newIds: string[]) => {
      const toRemove = selectedSubcategoryIds.filter((id) => !newIds.includes(id));
      const toAdd = newIds.filter((id) => !selectedSubcategoryIds.includes(id));
      toRemove.forEach((id) => toggleSubcategory(id));
      toAdd.forEach((id) => toggleSubcategory(id));
    },
    [selectedSubcategoryIds, toggleSubcategory],
  );

  useEffect(() => {
    if (searchQuery === '') {
      setLocalSearchQuery('');
    }
  }, [searchQuery]);

  useEffect(() => {
    const debounceMs = 400;
    const timer = setTimeout(() => {
      setSearchQuery(localSearchQuery);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [localSearchQuery, setSearchQuery]);

  const hasSelectedCategories = selectedCategoryIds.length > 0;

  return {
    localSearchQuery,
    setLocalSearchQuery,
    wrapperRef,
    buttonsContainerRef,
    filteredRootCategories,
    categorySelectValue,
    handleCategorySelect,
    subcategoryOptions,
    selectedSubcategoryIds,
    handleSubcategoryChange,
    setCategoryRef,
    selectedCategoryIds,
    toggleCategory,
    hasActiveFilters,
    resetFilters,
    containerWidth,
    buttonsOffsetLeft,
    generateLineWithChevrons,
    visibleSubcategories,
    toggleSubcategory,
    hasSelectedCategories,
  };
};
